import React, { useState, useEffect } from 'react';

const AchievementSystem = ({ gameHistory, movesCount, gameStatus }) => {
  const [points, setPoints] = useState(0);
  const [level, setLevel] = useState(1);
  const [achievements, setAchievements] = useState([]);
  const [showNotification, setShowNotification] = useState(null);

  // نشان‌های ممکن
  const allAchievements = [
    {
      id: 1,
      name: 'شروع کننده',
      description: 'اولین حرکت رو انجام بده',
      icon: '🎯',
      points: 10,
      condition: (history) => history.length >= 1,
      unlocked: false
    },
    {
      id: 2,
      name: 'ماهر کوچک',
      description: '۱۰ حرکت انجام بده',
      icon: '⭐',
      points: 25,
      condition: (history) => history.length >= 10,
      unlocked: false
    },
    {
      id: 3,
      name: 'استراتژیست',
      description: '۵۰ حرکت انجام بده',
      icon: '🏆',
      points: 50,
      condition: (history) => history.length >= 50,
      unlocked: false
    },
    {
      id: 4,
      name: 'کیش کننده',
      description: 'برای اولین بار کیش بده',
      icon: '♔',
      points: 30,
      condition: (history, status) => status.includes('کیش'),
      unlocked: false
    },
    {
      id: 5,
      name: 'قهرمان شطرنج',
      description: 'یک بازی رو برنده شو',
      icon: '👑',
      points: 100,
      condition: (history, status) => status.includes('کیش و مات'),
      unlocked: false
    },
    {
      id: 6,
      name: 'سرعت نور',
      description: '۱۰ حرکت در ۲ دقیقه انجام بده',
      icon: '⚡',
      points: 40,
      condition: (history) => history.length >= 10,
      unlocked: false
    },
    {
      id: 7,
      name: 'مهره دوست',
      description: 'با همه نوع مهره حرکت کن',
      icon: '♟️',
      points: 35,
      condition: (history) => {
        const pieces = new Set(history.map(move => move.piece));
        return pieces.size >= 6;
      },
      unlocked: false
    },
    {
      id: 8,
      name: 'بازیکن حرفه‌ای',
      description: 'به سطح ۵ برس',
      icon: '🎮',
      points: 75,
      condition: (history, status, lvl) => lvl >= 5,
      unlocked: false
    }
  ];

  // بررسی نشان‌ها
  useEffect(() => {
    const newAchievements = [...allAchievements];
    let newPoints = points;
    let newLevel = level;
    let newUnlocked = false;

    newAchievements.forEach(achievement => {
      if (!achievement.unlocked && achievement.condition(gameHistory, gameStatus, level)) {
        achievement.unlocked = true;
        newPoints += achievement.points;
        newUnlocked = true;
        
        // نمایش نوتیفیکیشن
        setShowNotification(achievement);
        setTimeout(() => setShowNotification(null), 3000);
      }
    });

    if (newUnlocked) {
      setAchievements(newAchievements.filter(a => a.unlocked));
      setPoints(newPoints);
      
      // محاسبه سطح جدید
      const newLevelCalc = Math.floor(newPoints / 100) + 1;
      if (newLevelCalc > newLevel) {
        setLevel(newLevelCalc);
      }
    }
  }, [gameHistory, gameStatus]);

  // نشان‌های باز شده
  const unlockedAchievements = achievements.filter(a => a.unlocked);
  const lockedAchievements = allAchievements.filter(a => !a.unlocked);

  return (
    <div style={styles.container}>
      {/* نوار وضعیت */}
      <div style={styles.statusBar}>
        <div style={styles.levelBadge}>
          <span style={styles.levelText}>سطح {level}</span>
        </div>
        <div style={styles.pointsContainer}>
          <span style={styles.pointsIcon}>⭐</span>
          <span style={styles.pointsText}>{points} امتیاز</span>
        </div>
        <div style={styles.achievementsCount}>
          <span style={styles.achievementsIcon}>🏆</span>
          <span style={styles.achievementsText}>{unlockedAchievements.length}/{allAchievements.length}</span>
        </div>
      </div>

      {/* پیشرفت سطح */}
      <div style={styles.progressSection}>
        <div style={styles.progressHeader}>
          <h3 style={styles.progressTitle}>پیشرفت به سطح بعدی</h3>
          <span style={styles.progressPercent}>{((points % 100) / 100 * 100).toFixed(0)}%</span>
        </div>
        <div style={styles.progressBar}>
          <div 
            style={{
              ...styles.progressFill,
              width: `${(points % 100) / 100 * 100}%`
            }}
          ></div>
        </div>
        <div style={styles.progressInfo}>
          <span>{points % 100} از 100 امتیاز</span>
          <span>سطح بعدی: {level + 1}</span>
        </div>
      </div>

      {/* نشان‌های باز شده */}
      <div style={styles.achievementsSection}>
        <h3 style={styles.sectionTitle}>نشان‌های تو 🏆</h3>
        <div style={styles.achievementsGrid}>
          {unlockedAchievements.map(achievement => (
            <div key={achievement.id} style={styles.achievementCard}>
              <div style={styles.achievementIcon}>{achievement.icon}</div>
              <div style={styles.achievementInfo}>
                <h4 style={styles.achievementName}>{achievement.name}</h4>
                <p style={styles.achievementDesc}>{achievement.description}</p>
                <span style={styles.achievementPoints}>+{achievement.points} امتیاز</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* نشان‌های قفل شده */}
      {lockedAchievements.length > 0 && (
        <div style={styles.lockedSection}>
          <h3 style={styles.sectionTitle}>نشان‌های در انتظار 🔒</h3>
          <div style={styles.lockedGrid}>
            {lockedAchievements.map(achievement => (
              <div key={achievement.id} style={styles.lockedCard}>
                <div style={styles.lockedIcon}>❓</div>
                <div style={styles.lockedInfo}>
                  <h4 style={styles.lockedName}>???</h4>
                  <p style={styles.lockedDesc}>این نشان رو هنوز باز نکردی</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* نوتیفیکیشن */}
      {showNotification && (
        <div style={styles.notification}>
          <div style={styles.notificationContent}>
            <span style={styles.notificationIcon}>{showNotification.icon}</span>
            <div>
              <h4 style={styles.notificationTitle}>نشان جدید!</h4>
              <p style={styles.notificationText}>{showNotification.name}</p>
              <span style={styles.notificationPoints}>+{showNotification.points} امتیاز</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// استایل‌ها
const styles = {
  container: {
    background: 'rgba(255, 255, 255, 0.95)',
    borderRadius: '16px',
    padding: '20px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.2)'
  },
  statusBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '25px',
    paddingBottom: '15px',
    borderBottom: '2px solid #f0f0f0'
  },
  levelBadge: {
    background: 'linear-gradient(135deg, #FFD700, #FFA500)',
    padding: '8px 16px',
    borderRadius: '20px',
    color: '#7d6608',
    fontWeight: 'bold',
    fontSize: '14px'
  },
  pointsContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: 'rgba(52, 152, 219, 0.1)',
    padding: '8px 16px',
    borderRadius: '20px'
  },
  pointsIcon: {
    fontSize: '18px'
  },
  pointsText: {
    fontWeight: '600',
    color: '#2c3e50'
  },
  achievementsCount: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: 'rgba(46, 204, 113, 0.1)',
    padding: '8px 16px',
    borderRadius: '20px'
  },
  achievementsIcon: {
    fontSize: '18px'
  },
  achievementsText: {
    fontWeight: '600',
    color: '#2c3e50'
  },
  progressSection: {
    marginBottom: '25px'
  },
  progressHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px'
  },
  progressTitle: {
    margin: '0',
    fontSize: '16px',
    color: '#2c3e50'
  },
  progressPercent: {
    fontWeight: 'bold',
    color: '#3498db'
  },
  progressBar: {
    width: '100%',
    height: '8px',
    background: '#ecf0f1',
    borderRadius: '4px',
    overflow: 'hidden'
  },
  progressFill: {
    height: '100%',
    background: 'linear-gradient(90deg, #3498db, #2ecc71)',
    borderRadius: '4px',
    transition: 'width 0.5s ease'
  },
  progressInfo: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '8px',
    fontSize: '12px',
    color: '#7f8c8d'
  },
  achievementsSection: {
    marginBottom: '20px'
  },
  sectionTitle: {
    margin: '0 0 15px 0',
    fontSize: '18px',
    color: '#2c3e50',
    borderBottom: '2px solid #3498db',
    paddingBottom: '8px'
  },
  achievementsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '15px'
  },
  achievementCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    padding: '15px',
    background: 'linear-gradient(135deg, #ffffff, #f8f9fa)',
    borderRadius: '12px',
    border: '2px solid #FFD700',
    boxShadow: '0 4px 12px rgba(255, 215, 0, 0.2)'
  },
  achievementIcon: {
    fontSize: '32px',
    minWidth: '40px'
  },
  achievementInfo: {
    flex: '1'
  },
  achievementName: {
    margin: '0 0 5px 0',
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#2c3e50'
  },
  achievementDesc: {
    margin: '0 0 8px 0',
    fontSize: '12px',
    color: '#7f8c8d'
  },
  achievementPoints: {
    fontSize: '12px',
    color: '#27ae60',
    fontWeight: 'bold'
  },
  lockedSection: {
    marginBottom: '20px'
  },
  lockedGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '15px'
  },
  lockedCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    padding: '15px',
    background: 'linear-gradient(135deg, #f8f9fa, #e9ecef)',
    borderRadius: '12px',
    border: '2px solid #bdc3c7',
    opacity: '0.7'
  },
  lockedIcon: {
    fontSize: '32px',
    minWidth: '40px',
    opacity: '0.5'
  },
  lockedInfo: {
    flex: '1'
  },
  lockedName: {
    margin: '0 0 5px 0',
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#bdc3c7'
  },
  lockedDesc: {
    margin: '0',
    fontSize: '12px',
    color: '#bdc3c7'
  },
  notification: {
    position: 'fixed',
    top: '20px',
    right: '20px',
    background: 'linear-gradient(135deg, #2ecc71, #27ae60)',
    color: 'white',
    padding: '15px 20px',
    borderRadius: '12px',
    boxShadow: '0 8px 25px rgba(46, 204, 113, 0.4)',
    zIndex: '1000',
    animation: 'slideIn 0.5s ease'
  },
  notificationContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px'
  },
  notificationIcon: {
    fontSize: '32px'
  },
  notificationTitle: {
    margin: '0 0 5px 0',
    fontSize: '16px',
    fontWeight: 'bold'
  },
  notificationText: {
    margin: '0 0 5px 0',
    fontSize: '14px'
  },
  notificationPoints: {
    fontSize: '12px',
    opacity: '0.9'
  }
};

export default AchievementSystem;